import React, { useState } from 'react';
import { QuoteData, Liability } from '../types';
import { ArrowRight, Plus, Trash2, ChevronDown, ChevronUp, ArrowDown, ArrowUp } from 'lucide-react';
import { UmlLogo } from './UmlLogo';
import { formatCurrency, formatPercent, sumLiabilitiesPaidOff, sumAllMonthlyLiabilityPayments } from '../lib/calculations';
interface QuoteFormProps {
  initialData: QuoteData;
  onSubmit: (data: QuoteData) => void;
}
const inputClass = 'w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/30 transition-colors text-sm';
const tableInputClass = 'w-full px-2 py-1.5 bg-transparent border border-transparent rounded text-gray-900 placeholder:text-gray-300 focus:outline-none focus:border-gold focus:bg-white hover:border-gray-200 transition-colors text-sm';
const labelClass = 'block text-xs font-medium text-gray-600 mb-1.5';
interface FieldProps {
  label: string;
  children: React.ReactNode;
}
function Field({
  label,
  children
}: FieldProps) {
  return <div data-id="element-160">
      <label className={labelClass} data-id="element-161">{label}</label>
      {children}
    </div>;
}
interface SectionHeaderProps {
  title: string;
  description?: string;
}
function SectionHeader({
  title,
  description
}: SectionHeaderProps) {
  return <div className="mb-5 pb-2 border-b border-gray-200" data-id="element-162">
      <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wider" data-id="element-163">
        {title}
      </h3>
      {description && <p className="text-xs text-gray-500 mt-1 normal-case font-normal tracking-normal" data-id="element-164">
          {description}
        </p>}
    </div>;
}
function makeId() {
  return `liab-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}
function DeltaBadge({
  value,
  type,
  label
}: {
  value: number;
  type: 'rate' | 'payment';
  label: string;
}) {
  if (!value) return null;
  // > 0 means a drop in rate or payment (which is good/savings)
  const isPositive = value > 0;
  const color = isPositive ? 'text-gold bg-gold/10 border-gold/30' : 'text-gray-500 bg-gray-50 border-gray-200';
  const Icon = isPositive ? ArrowDown : ArrowUp;
  const formatted = type === 'rate' ? formatPercent(Math.abs(value)) : formatCurrency(Math.abs(value));
  return <div className={`hidden md:flex absolute left-1/2 -translate-x-1/2 items-center gap-1 px-2.5 py-1 border rounded-full text-[10px] font-medium z-10 shadow-sm whitespace-nowrap ${color}`}
  // Center vertically on the input itself: label (~22px) + input center (~19px)
  style={{
    top: '34px'
  }} aria-label={`${label} ${formatted}`} data-id="element-165">
      <Icon className="w-3 h-3" data-id="element-166" />
      {formatted}
    </div>;
}
export function QuoteForm({
  initialData,
  onSubmit
}: QuoteFormProps) {
  const [formData, setFormData] = useState<QuoteData>(initialData);
  const [isLoExpanded, setIsLoExpanded] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    let parsedValue: string | number = value;
    if (type === 'number') {
      parsedValue = value === '' ? 0 : Number(value);
    }
    setFormData(prev => ({
      ...prev,
      [name]: parsedValue
    }));
  };
  // Liability handlers
  const addLiability = () => {
    setFormData(prev => ({
      ...prev,
      liabilities: [...prev.liabilities, {
        id: makeId(),
        debtName: '',
        creditor: '',
        currentBalance: 0,
        monthlyPayment: 0,
        beingPaidOff: false
      }]
    }));
  };
  const removeLiability = (id: string) => {
    setFormData(prev => ({
      ...prev,
      liabilities: prev.liabilities.filter(l => l.id !== id)
    }));
  };
  const updateLiability = <K extends keyof Liability,>(id: string, key: K, value: Liability[K]) => {
    setFormData(prev => ({
      ...prev,
      liabilities: prev.liabilities.map(l => l.id === id ? {
        ...l,
        [key]: value
      } : l)
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };
  // Derived liability summary values for inline feedback
  const totalPaidOff = sumLiabilitiesPaidOff(formData.liabilities);
  const totalMonthlyLiabilities = sumAllMonthlyLiabilityPayments(formData.liabilities);
  // Shared per-row two-column grid for the comparison section
  const compareRowClass = 'grid grid-cols-1 md:grid-cols-2 gap-x-8 md:gap-x-12 gap-y-5 relative z-10';
  return <div className="max-w-5xl mx-auto" data-id="element-167">
      {/* UML brand bar */}
      <div className="flex items-center justify-between mb-6 px-2" data-id="element-168">
        <div className="text-sm" data-id="element-169">
          <UmlLogo variant="light" data-id="element-170" />
        </div>
        <div className="text-xs text-gray-400 tracking-wider uppercase" data-id="element-171">
          Refinance Proposal Builder
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-10 shadow-sm" data-id="element-172">
        {/* Header */}
        <div className="mb-8" data-id="element-173">
          <h2 className="font-serif text-2xl text-gray-900 mb-1.5" data-id="element-174">
            Create a proposal
          </h2>
          <p className="text-sm text-gray-500" data-id="element-175">
            Enter the borrower and loan details to generate a private-client
            proposal.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-10" data-id="element-176">
          {/* Section 1: Borrower & Property */}
          <section data-id="element-177">
            <SectionHeader title="Borrower & Property" data-id="element-178" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-179">
              <Field label="Borrower First Name" data-id="element-180">
                <input type="text" name="borrowerFirstName" value={formData.borrowerFirstName} onChange={handleChange} required className={inputClass} data-id="element-181" />
              </Field>
              <Field label="Property Value" data-id="element-182">
                <div className="relative" data-id="element-183">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" data-id="element-184">
                    $
                  </span>
                  <input type="number" name="propertyValue" value={formData.propertyValue || ''} onChange={handleChange} required min="0" className={`${inputClass} pl-6`} data-id="element-185" />
                </div>
              </Field>
              <Field label="Refinance Type" data-id="element-186">
                <select name="refinanceType" value={formData.refinanceType} onChange={handleChange} className={inputClass} data-id="element-187">
                  <option value="Cash-Out" data-id="element-188">Cash-Out</option>
                  <option value="Rate-Term" data-id="element-189">Rate-Term</option>
                </select>
              </Field>
              <Field label="Loan Type" data-id="element-190">
                <select name="loanType" value={formData.loanType} onChange={handleChange} className={inputClass} data-id="element-191">
                  <option value="Conventional" data-id="element-192">Conventional</option>
                  <option value="FHA" data-id="element-193">FHA</option>
                  <option value="VA" data-id="element-194">VA</option>
                  <option value="Jumbo" data-id="element-195">Jumbo</option>
                  <option value="USDA" data-id="element-196">USDA</option>
                </select>
              </Field>
              <Field label="Lender Name" data-id="element-197">
                <input type="text" name="lenderName" value={formData.lenderName} onChange={handleChange} required className={inputClass} data-id="element-198" />
              </Field>
            </div>
          </section>

          {/* Section 2: Side-by-Side Loan Comparison */}
          <section data-id="element-199">
            <SectionHeader title="Loan Comparison" description="Enter the existing mortgage on the left and the new offer on the right." data-id="element-200" />

            <div className="space-y-5 relative" data-id="element-201">
              {/* Global vertical divider */}
              <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gray-100 -translate-x-1/2 z-0" data-id="element-202"></div>

              {/* Column Headers */}
              <div className={compareRowClass} data-id="element-203">
                <div className="flex items-center justify-between pb-2 border-b border-gray-200" data-id="element-204">
                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-205">
                    Current Loan
                  </h4>
                </div>
                <div className="flex items-center justify-between pb-2 border-b border-gold/40" data-id="element-206">
                  <h4 className="text-xs font-semibold text-gold uppercase tracking-wider" data-id="element-207">
                    Proposed Loan
                  </h4>
                  <span className="bg-gold/10 text-gold text-[9px] px-2 py-0.5 rounded-full tracking-wider uppercase font-bold" data-id="element-208">
                    New
                  </span>
                </div>
              </div>

              {/* Row 1: Rate */}
              <div className={compareRowClass} data-id="element-209">
                <Field label="Interest Rate" data-id="element-210">
                  <div className="relative" data-id="element-211">
                    <input type="number" name="currentRate" value={formData.currentRate || ''} onChange={handleChange} required step="0.001" min="0" className={`${inputClass} pr-7`} data-id="element-212" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" data-id="element-213">
                      %
                    </span>
                  </div>
                </Field>
                <Field label="Interest Rate" data-id="element-214">
                  <div className="relative" data-id="element-215">
                    <input type="number" name="proposedRate" value={formData.proposedRate || ''} onChange={handleChange} required step="0.001" min="0" className={`${inputClass} pr-7`} data-id="element-216" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" data-id="element-217">
                      %
                    </span>
                  </div>
                </Field>
                {formData.currentRate > 0 && formData.proposedRate > 0 && <DeltaBadge value={formData.currentRate - formData.proposedRate} type="rate" label="Rate change" data-id="element-218" />}
              </div>

              {/* Row 2: Payment */}
              <div className={compareRowClass} data-id="element-219">
                <Field label="Monthly Payment (P&I)" data-id="element-220">
                  <div className="relative" data-id="element-221">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" data-id="element-222">
                      $
                    </span>
                    <input type="number" name="currentPayment" value={formData.currentPayment || ''} onChange={handleChange} required min="0" className={`${inputClass} pl-6`} data-id="element-223" />
                  </div>
                </Field>
                <Field label="Monthly Payment (P&I)" data-id="element-224">
                  <div className="relative" data-id="element-225">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" data-id="element-226">
                      $
                    </span>
                    <input type="number" name="proposedPayment" value={formData.proposedPayment || ''} onChange={handleChange} required min="0" className={`${inputClass} pl-6`} data-id="element-227" />
                  </div>
                </Field>
                {formData.currentPayment > 0 && formData.proposedPayment > 0 && <DeltaBadge value={formData.currentPayment - formData.proposedPayment} type="payment" label="Payment change" data-id="element-228" />}
              </div>

              {/* Row 3: Balance / Loan Amount */}
              <div className={compareRowClass} data-id="element-229">
                <Field label="Remaining Balance" data-id="element-230">
                  <div className="relative" data-id="element-231">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" data-id="element-232">
                      $
                    </span>
                    <input type="number" name="currentBalance" value={formData.currentBalance || ''} onChange={handleChange} required min="0" className={`${inputClass} pl-6`} data-id="element-233" />
                  </div>
                </Field>
                <Field label="New Loan Amount" data-id="element-234">
                  <div className="relative" data-id="element-235">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" data-id="element-236">
                      $
                    </span>
                    <input type="number" name="proposedLoanAmount" value={formData.proposedLoanAmount || ''} onChange={handleChange} required min="0" className={`${inputClass} pl-6`} data-id="element-237" />
                  </div>
                </Field>
              </div>

              {/* Row 4: Term */}
              <div className={compareRowClass} data-id="element-238">
                <Field label="Term (Years)" data-id="element-239">
                  <input type="number" name="currentTerm" value={formData.currentTerm || ''} onChange={handleChange} required min="1" className={inputClass} data-id="element-240" />
                </Field>
                <Field label="Term (Years)" data-id="element-241">
                  <input type="number" name="proposedTerm" value={formData.proposedTerm || ''} onChange={handleChange} required min="1" className={inputClass} data-id="element-242" />
                </Field>
              </div>

              {/* Extras row: side-specific fields with no opposite-side pair */}
              <div className={compareRowClass} data-id="element-243">
                <Field label="Product Description" data-id="element-244">
                  <input type="text" name="currentProduct" value={formData.currentProduct} onChange={handleChange} required placeholder="e.g. 30-year fixed" className={inputClass} data-id="element-245" />
                </Field>
                <div className="grid grid-cols-2 gap-4" data-id="element-246">
                  <Field label="Cash to Borrower" data-id="element-247">
                    <div className="relative" data-id="element-248">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" data-id="element-249">
                        $
                      </span>
                      <input type="number" name="cashToBorrower" value={formData.cashToBorrower || ''} onChange={handleChange} required min="0" className={`${inputClass} pl-6`} data-id="element-250" />
                    </div>
                  </Field>
                  <Field label="Est. Days to Close" data-id="element-251">
                    <input type="number" name="daysToClose" value={formData.daysToClose || ''} onChange={handleChange} required min="1" className={inputClass} data-id="element-252" />
                  </Field>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3: Borrower Liabilities */}
          <section data-id="element-253">
            <SectionHeader title="Borrower Liabilities" description="Add the borrower's current debts. Check 'Paid Off' for any debts that will be paid off through this refinance." data-id="element-254" />

            <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-255">
              {/* Table header */}
              <div className="grid grid-cols-[1.4fr_1.4fr_1fr_1fr_0.6fr_32px] gap-3 px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-[10px] font-semibold text-gray-600 uppercase tracking-wider" data-id="element-256">
                <div data-id="element-257">Debt Name</div>
                <div data-id="element-258">Creditor</div>
                <div data-id="element-259">Current Balance</div>
                <div data-id="element-260">Monthly Payment</div>
                <div className="text-center" data-id="element-261">Paid Off</div>
                <div data-id="element-262"></div>
              </div>

              {/* Rows */}
              {formData.liabilities.length === 0 && <div className="px-4 py-8 text-center text-sm text-gray-400" data-id="element-263">
                  No liabilities added yet.
                </div>}
              {formData.liabilities.map(liability => <div key={liability.id} className="grid grid-cols-[1.4fr_1.4fr_1fr_1fr_0.6fr_32px] gap-3 px-4 py-2 border-b border-gray-100 last:border-b-0 items-center hover:bg-gray-50/50 transition-colors" data-id="element-264">
                  <input type="text" value={liability.debtName} onChange={e => updateLiability(liability.id, 'debtName', e.target.value)} placeholder="e.g. Credit Card" className={tableInputClass} data-id="element-265" />
                  <input type="text" value={liability.creditor} onChange={e => updateLiability(liability.id, 'creditor', e.target.value)} placeholder="e.g. Chase" className={tableInputClass} data-id="element-266" />
                  <div className="relative" data-id="element-267">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none" data-id="element-268">
                      $
                    </span>
                    <input type="number" value={liability.currentBalance || ''} onChange={e => updateLiability(liability.id, 'currentBalance', e.target.value === '' ? 0 : Number(e.target.value))} min="0" placeholder="0" className={`${tableInputClass} pl-5`} data-id="element-269" />
                  </div>
                  <div className="relative" data-id="element-270">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none" data-id="element-271">
                      $
                    </span>
                    <input type="number" value={liability.monthlyPayment || ''} onChange={e => updateLiability(liability.id, 'monthlyPayment', e.target.value === '' ? 0 : Number(e.target.value))} min="0" placeholder="0" className={`${tableInputClass} pl-5`} data-id="element-272" />
                  </div>
                  <div className="flex justify-center" data-id="element-273">
                    <input type="checkbox" checked={liability.beingPaidOff} onChange={e => updateLiability(liability.id, 'beingPaidOff', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-gold focus:ring-gold/40 cursor-pointer accent-gold" data-id="element-274" />
                  </div>
                  <button type="button" onClick={() => removeLiability(liability.id)} className="text-gray-300 hover:text-red-500 transition-colors p-1 rounded" aria-label="Remove liability" data-id="element-275">
                    <Trash2 className="w-4 h-4" data-id="element-276" />
                  </button>
                </div>)}

              {/* Footer: add row + totals */}
              <div className="px-4 py-3 bg-gray-50/60 border-t border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-id="element-277">
                <button type="button" onClick={addLiability} className="inline-flex items-center gap-1.5 text-sm text-gray-700 hover:text-gold transition-colors font-medium" data-id="element-278">
                  <Plus className="w-4 h-4" data-id="element-279" />
                  Add Liability
                </button>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-600" data-id="element-280">
                  <div data-id="element-281">
                    <span className="text-gray-400" data-id="element-282">Total monthly: </span>
                    <span className="font-semibold text-gray-900" data-id="element-283">
                      {formatCurrency(totalMonthlyLiabilities)}
                    </span>
                  </div>
                  <div data-id="element-284">
                    <span className="text-gray-400" data-id="element-285">Being paid off: </span>
                    <span className="font-semibold text-gold" data-id="element-286">
                      {formatCurrency(totalPaidOff)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4: Loan Officer Details (Accordion) */}
          <section data-id="element-287">
            <SectionHeader title="Loan Officer Info" description="These details appear in the proposal footer." data-id="element-288" />
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white" data-id="element-289">
              <button type="button" onClick={() => setIsLoExpanded(!isLoExpanded)} className="w-full flex justify-between items-center px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-sm font-medium text-gray-700" data-id="element-290">
                <span data-id="element-291">
                  {formData.loName}
                  <span className="text-gray-400 font-normal" data-id="element-292">
                    {' · NMLS# '}
                    {formData.loNmls}
                  </span>
                </span>
                {isLoExpanded ? <ChevronUp className="w-4 h-4 text-gray-500" data-id="element-293" /> : <ChevronDown className="w-4 h-4 text-gray-500" data-id="element-294" />}
              </button>
              {isLoExpanded && <div className="p-5 grid grid-cols-1 md:grid-cols-4 gap-5 border-t border-gray-200" data-id="element-295">
                  <Field label="Name" data-id="element-296">
                    <input type="text" name="loName" value={formData.loName} onChange={handleChange} required className={inputClass} data-id="element-297" />
                  </Field>
                  <Field label="NMLS #" data-id="element-298">
                    <input type="text" name="loNmls" value={formData.loNmls} onChange={handleChange} required className={inputClass} data-id="element-299" />
                  </Field>
                  <Field label="Phone" data-id="element-300">
                    <input type="text" name="loPhone" value={formData.loPhone} onChange={handleChange} required className={inputClass} data-id="element-301" />
                  </Field>
                  <Field label="Email" data-id="element-302">
                    <input type="email" name="loEmail" value={formData.loEmail} onChange={handleChange} required className={inputClass} data-id="element-303" />
                  </Field>
                </div>}
            </div>
          </section>

          <div className="pt-4 flex justify-end border-t border-gray-100" data-id="element-304">
            <button type="submit" className="group inline-flex items-center gap-2 bg-charcoal-900 text-white px-6 py-2.5 rounded-md font-medium hover:bg-charcoal-800 transition-colors text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold" data-id="element-305">
              Generate Proposal
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" data-id="element-306" />
            </button>
          </div>
        </form>
      </div>
    </div>;
}